import { useEffect, useRef } from 'react';
import { useForm, type FieldValues, type UseFormReturn, type Resolver, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType } from 'zod';

import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import type {
  OnboardingData,
  OnboardingSectionKey,
} from 'features/onboarding/types/onboarding.types';

interface UseStepFormOptions<TInput extends FieldValues> {
  section: OnboardingSectionKey;
  schema: ZodType<TInput>;
  /**
   * Optional gate that runs after schema validation passes, both on the
   * "Next" button and on stepper-initiated forward jumps. Return `false` to
   * block advancement (e.g. a required document hasn't been uploaded). The
   * callback is responsible for surfacing its own error UI.
   */
  beforeAdvance?: () => boolean;
}

export type UseStepFormReturn<TInput extends FieldValues> = UseFormReturn<TInput> & {
  /** Submit handler bound to handleSubmit. Persists values, advances step. */
  submit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  /** Go to the previous step. */
  goBack: () => void;
};

/**
 * Wraps react-hook-form + zod + the onboarding context to remove duplicated
 * boilerplate from each Step component. A step file only needs to declare its
 * fields and call `submit` from its <form>.
 */
export function useStepForm<TInput extends FieldValues>({
  section,
  schema,
  beforeAdvance,
}: UseStepFormOptions<TInput>): UseStepFormReturn<TInput> {
  const { data, updateSection, goNext, goBack, registerStepGuard } = useOnboarding();

  const sectionData = data[section] as unknown as TInput;

  const form = useForm<TInput>({
    resolver: zodResolver(schema) as unknown as Resolver<TInput>,
    defaultValues: sectionData as DefaultValues<TInput>,
    values: sectionData,
    mode: 'onTouched',
  });

  // Keep the latest gate in a ref so the stepper guard (registered once)
  // always runs the freshest closure without re-registering each render.
  const beforeAdvanceRef = useRef(beforeAdvance);
  beforeAdvanceRef.current = beforeAdvance;

  const submit = form.handleSubmit((values) => {
    if (beforeAdvanceRef.current && !beforeAdvanceRef.current()) return;
    updateSection(section, values as Partial<OnboardingData[typeof section]>);
    goNext();
  });

  // Register a guard so forward jumps via the stepper run the same zod
  // validation as the step's "Next" button. If validation passes we also
  // persist the currently-typed values into the onboarding context so they
  // aren't lost on the way out.
  useEffect(() => {
    registerStepGuard(async () => {
      const valid = await form.trigger();
      if (!valid) return false;
      if (beforeAdvanceRef.current && !beforeAdvanceRef.current()) return false;
      updateSection(section, form.getValues() as Partial<OnboardingData[typeof section]>);
      return true;
    });
    return () => registerStepGuard(null);
  }, [registerStepGuard, form, section, updateSection]);

  return Object.assign(form, { submit, goBack }) as UseStepFormReturn<TInput>;
}
